<template>
  <div id="guide">
    <div class="breadcrumb">
      <h2>
        <span class="crumb">
          <router-link :to="{ name: 'trips' }">Dashboard</router-link>
          <span class="icon">></span>
          <router-link :to="{ name: 'trips' }">Trips</router-link>
          <span class="icon">></span>
        </span>
        <span>{{ sectionTypeVal === "Group" ? "Book a Group" : "Book a Trip" }}</span>
      </h2>
    </div>
    <div class="panels">
      <div class="panel-left-flex sections">
        <div class="section">
          <div v-if="sectionTypeIsSummary" class="summary">
            <span class="tag">1. Type</span>
            <span class="choice">{{ sectionTypeVal }}<a @click="sectionTypeReset">Change</a></span>
          </div>
          <div v-else class="selection">
            <h3>1. What type of trip do you have?</h3>
            <div class="radiobuttons">
              <div class="radiolabel" :class="{ on: sectionTypeVal == 'Standard' }" @click="sectionTypeSelect('Standard')">
                <div class="radio" />
                <div class="inner">
                  <label>Standard<span class="pill">Popular</span></label>
                  <p>Flight, hotel and car bookings</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionTypeVal == 'Group' }" @click="sectionTypeSelect('Group')">
                <div class="radio" />
                <div class="inner">
                  <label>Group</label>
                  <p>More than 8 people traveling</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionTypeVal == 'Offsite' }" @click="sectionTypeSelect('Offsite')">
                <div class="radio" />
                <div class="inner">
                  <label>Offsite</label>
                  <p>Corporate offsites, conferences and retreats</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionTypeVal == 'Premium' }" @click="sectionTypeSelect('Premium')">
                <div class="radio" />
                <div class="inner">
                  <label>VIP</label>
                  <p>Premium classes and private jets</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionTypeVal == 'Custom' }" @click="sectionTypeSelect('Custom')">
                <div class="radio" />
                <div class="inner">
                  <label>Custom</label>
                  <p>Any complicated travel request</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="sectionMethodIsShow" class="section">
          <div v-if="sectionMethodIsSummary" class="summary">
            <span class="tag">2. Method</span>
            <span class="choice">{{ sectionMethodVal }}<a @click="sectionMethodReset">Change</a></span>
          </div>
          <div v-else class="selection">
            <h3>2. How would you like to book?</h3>
            <div class="radiobuttons">
              <div
                class="radiolabel"
                :class="{
                  on: sectionMethodVal == 'Self Booking',
                  noselfbook: !isSelfBookAllowed,
                }"
                @click="sectionMethodSelect('Self Booking')"
              >
                <div class="radio" />
                <div class="inner">
                  <label>Self Booking<span v-if="!isSelfBookAllowed" class="pill dis">Disabled</span><span v-else class="pill">Popular</span></label>
                  <p>Select your own flights, hotels, etc. and book immediately.</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionMethodVal == 'Assisted Booking' }" @click="sectionMethodSelect('Assisted Booking')">
                <div class="radio" />
                <div class="inner">
                  <label>Assisted Booking<span v-if="!isSelfBookAllowed" class="pill">Popular</span></label>
                  <p>Receive flight, hotel, etc. suggestions from our team.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="sectionItineraryIsShow" class="section">
          <div v-if="sectionItineraryIsSummary" class="summary">
            <span class="tag">2. Itinerary</span>
            <span class="choice">{{ sectionItineraryVal }}<a @click="sectionItineraryReset">Change</a></span>
          </div>
          <div v-else class="selection">
            <h3>2. Is your schedule already decided?</h3>
            <div class="radiobuttons">
              <div class="radiolabel" :class="{ on: sectionItineraryVal == 'Decided' }" @click="sectionItinerarySelect('Decided')">
                <div class="radio" />
                <div class="inner">
                  <label>Yes</label>
                  <p>The places and dates are already decided.</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionItineraryVal == 'Undecided' }" @click="sectionItinerarySelect('Undecided')">
                <div class="radio" />
                <div class="inner">
                  <label>No</label>
                  <p>The places and dates are not fully decided and need discussion.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="sectionOrgIsShow" class="section">
          <div v-if="sectionOrgIsSummary" class="summary">
            <span class="tag">3. Organization</span>
            <span class="choice">{{ selectedOrgName }}<a v-if="organizations.length > 1" @click="sectionOrgReset">Change</a></span>
          </div>
          <div v-else class="selection">
            <h3>3. Which organization is this trip for?</h3>
            <div v-if="organizations.length > 1" class="form-row">
              <select required @change="sectionOrgSelectOrganization($event.target.value)">
                <option value="" disabled selected>Which organization?</option>
                <option v-for="o in organizations" :key="o.id" :value="o.id">{{ o.name }}</option>
              </select>
            </div>
            <div v-if="sectionOrgIsShowIdentifier">
              <label>{{ organizationIdentifierLabel || "Accounting Identifier" }}</label>
              <div class="form-row">
                <input v-model="identifier" type="text" class="ident" placeholder="Mandatory" />
              </div>
              <button class="button alt" @click="sectionOrgSelectIdentifier">Next</button>
            </div>
          </div>
        </div>
        <div v-if="sectionContactIsShow" class="section">
          <div v-if="sectionContactIsSummary" class="summary">
            <span class="tag">{{ contactStepNumber }}. Contact</span>
            <span class="choice">{{ sectionContactValLabel }}<a @click="sectionContactReset">Change</a></span>
          </div>
          <div v-else class="selection">
            <h3>{{ contactStepNumber }}. Who is the contact for this trip?</h3>
            <div class="radiobuttons">
              <div class="radiolabel" :class="{ on: sectionContactVal == 'You' }" @click="sectionContactSelect('You')">
                <div class="radio" />
                <div class="inner">
                  <label>You are the main contact</label>
                  <p>You will receive booking, alert and support notifications.</p>
                </div>
              </div>
              <div v-if="organizationId" class="radiolabel" :class="{ on: sectionContactVal == 'Colleague' }" @click="sectionContactSelect('Colleague')">
                <div class="radio" />
                <div class="inner">
                  <label>Assign someone from within your organization</label>
                  <p>Your colleague will receive booking, alert and support notifications.</p>
                </div>
              </div>
              <div class="radiolabel" :class="{ on: sectionContactVal == 'External' }" @click="sectionContactSelect('External')">
                <div class="radio" />
                <div class="inner">
                  <label>Assign someone external to your organization</label>
                  <p>A consultant, job candidate, or similar will receive notifications.</p>
                </div>
              </div>
            </div>
            <select v-if="sectionContactIsShowCustomers" v-model="customerId" required>
              <option value="" disabled>Which colleague?</option>
              <option v-for="(c, i) in customers" :key="i" :value="c.customer_id">
                {{ c.customer_name }}
              </option>
            </select>
            <div v-if="sectionContactIsShowExternal">
              <label>What is the name and email?</label>
              <div class="form-row">
                <input v-model="customerName" type="text" placeholder="Full Name" />
              </div>
              <div class="form-row">
                <input v-model="customerEmail" type="text" placeholder="Email Address" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="sectionWizardIsShow" class="section wiz">
          <p>{{ sectionWizardNote }}</p>
          <button @click="sectionWizardGo">Next <span class="icon">&#x3e;</span> Details</button>
        </div>
      </div>
      <FAQ
        class="panel-right-40-percent"
        :faqs="[
          {
            question: 'What type of travel do we book?',
            answer: 'Absolutely everything. We offer self-booking for standard trips, assisted booking for any trip, and a concierge service for complex trips.',
          },
          {
            question: 'How does self booking work?',
            answer: 'You choose your flights and hotels, then our system books immediately at the lowest available prices through major partners such as Expedia.',
          },
          {
            question: 'How does assisted booking work?',
            answer: 'You tell us your places and dates, whether you need flights, hotels and cars, any additional preferences, then we suggest the best itinerary.',
          },
        ]"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { useRouter } from "vue-router";
import useApi from "../utils/apiCalls.js";
import { setCurrency } from "../utils/money.js";
import FAQ from "../components/common/FaqPanel.vue";

const router = useRouter();

const userId = inject("userId");
const userIsExpert = inject("userIsExpert");

const emit = defineEmits(["error", "update:customerId", "update:customerName", "update:customerEmail", "update:organizationId", "update:identifier", "update:stage", "update:template", "update:wizard"]);
const { apiGetRequest } = useApi(emit);

const isSelfBookAllowed = ref(true);
const loadIsSelfServeAllowed = () => {
  apiGetRequest({
    path: "/customer-is-self-book",
    onSuccess: data => {
      isSelfBookAllowed.value = data;
    },
  });
};
loadIsSelfServeAllowed();

const identifier = ref(null);
const organizationId = ref(null);
const organizationIdentifierLabel = ref(null);
const organizationIsIdentifierRequired = ref(false);
const organizations = ref([]);

const loadOrganizations = () => {
  // Check for lander redirect first — non-member lander users should never reach the wizard
  if (!userIsExpert.value) {
    apiGetRequest({
      path: "/customer/lander",
      onSuccess: landerData => {
        if (landerData?.slug) {
          router.push({ name: "lander", params: { slug: landerData.slug } });
          return;
        }
        // No lander — proceed with loading organizations for the wizard
        loadOrganizationsData();
      },
    });
  } else {
    loadOrganizationsData();
  }
};

const loadOrganizationsData = () => {
  apiGetRequest({
    path: "/organizations",
    onSuccess: data => {
      organizations.value = data;
      if (organizations.value.length === 1) {
        organizationId.value = organizations.value[0].id;
        sectionOrgSelectOrganization(organizationId.value);
        // Explicitly emit to parent in case watcher doesn't trigger
        emit("update:organizationId", organizationId.value);
      }
    },
  });
};
loadOrganizations();
watch(
  () => organizationId.value,
  val => {
    emit("update:organizationId", val);
    // Set display currency for the selected org. Backend money is canonical USD;
    // /organizations/{id} already returns the code + rate (same endpoint as SettingsPolicies).
    if (val) {
      apiGetRequest({
        path: `/organizations/${val}`,
        onSuccess: data => setCurrency(data.currency, data.currency_rate),
      });
    }
  }
);

const customerId = ref("");
const customerName = ref(null);
const customerEmail = ref(null);
const customers = ref([]);
const loadCustomers = () => {
  apiGetRequest({
    path: `/organizations/${organizationId.value}/members`,
    onSuccess: data => {
      customers.value = data;
    },
  });
};
watch(
  () => customerId.value,
  val => {
    emit("update:customerId", val);
  }
);
watch(
  () => customerName.value,
  val => {
    emit("update:customerName", val);
  }
);
watch(
  () => customerEmail.value,
  val => {
    emit("update:customerEmail", val);
  }
);

const sectionTypeVal = ref(null);
const sectionTypeIsStandard = computed(() => {
  return sectionTypeVal.value == "Standard";
});
const sectionTypeIsSummary = computed(() => {
  return sectionTypeVal.value !== null;
});
const sectionTypeSelect = val => {
  sectionTypeVal.value = val;
};
const sectionTypeReset = () => {
  sectionTypeVal.value = null;
  sectionMethodVal.value = null;
  sectionItineraryVal.value = null;
  sectionOrgReset();
};

const sectionMethodVal = ref(null);
const sectionMethodIsShow = computed(() => {
  return sectionTypeIsSummary.value === true && sectionTypeIsStandard.value === true;
});
const sectionMethodIsSummary = computed(() => {
  return sectionMethodVal.value !== null;
});
const sectionMethodIsSelfBooking = computed(() => {
  return sectionMethodVal.value == "Self Booking";
});
const sectionMethodSelect = val => {
  if (isSelfBookAllowed.value === false && val == "Self Booking") {
    alert("Your organization has disabled the Self Booking options as a part of your travel policy. Please select Assisted Booking.");
    return;
  }
  sectionMethodVal.value = val;
};
const sectionMethodReset = () => {
  sectionMethodVal.value = null;
  sectionItineraryVal.value = null;
  sectionOrgReset();
};

const sectionItineraryVal = ref(null);
const sectionItineraryIsShow = computed(() => {
  return sectionTypeIsStandard.value === false && sectionTypeIsSummary.value === true;
});
const sectionItineraryIsSummary = computed(() => {
  return sectionItineraryVal.value !== null;
});
const sectionItineraryIsTextBased = computed(() => {
  return sectionItineraryVal.value == "Undecided";
});
const sectionItinerarySelect = val => {
  sectionItineraryVal.value = val;
};
const sectionItineraryReset = () => {
  sectionItineraryVal.value = null;
  sectionOrgReset();
};

const sectionPurposeIsIdentifierDone = ref(false);

const sectionOrgIsShow = computed(() => {
  if (shouldSkipOrgStep.value) {
    return false;
  }
  return isPreOrgStepComplete.value;
});

const sectionOrgIsShowIdentifier = computed(() => {
  return organizationId.value != null && organizationIsIdentifierRequired.value === true;
});

// Helper: is the step before org complete?
const isPreOrgStepComplete = computed(() => {
  return sectionItineraryIsShow.value ? sectionItineraryIsSummary.value : sectionMethodIsSummary.value;
});

// Should we skip the organization step entirely?
const shouldSkipOrgStep = computed(() => {
  // Skip for experts with no organizations
  if (userIsExpert.value && organizations.value.length === 0) {
    return true;
  }
  // Skip for users with exactly 1 org that doesn't require identifier
  if (organizations.value.length === 1 && !organizationIsIdentifierRequired.value) {
    return true;
  }
  return false;
});

// Dynamic step number for Contact (3 if org skipped, 4 otherwise)
const contactStepNumber = computed(() => (shouldSkipOrgStep.value ? 3 : 4));

const sectionOrgIsSummary = computed(() => {
  return organizationId.value !== null && (!sectionOrgIsShowIdentifier.value || sectionPurposeIsIdentifierDone.value);
});

const selectedOrgName = computed(() => {
  const org = organizations.value.find(o => o.id === organizationId.value);
  return org?.name ?? "";
});

const sectionOrgSelectOrganization = id => {
  organizationId.value = Number(id);
  const org = organizations.value.find(o => o.id == id);
  if (org) {
    organizationIdentifierLabel.value = org.identifier_label;
    organizationIsIdentifierRequired.value = Boolean(org.is_identifier_required);
  }
};

const sectionOrgSelectIdentifier = () => {
  if (identifier.value != null && identifier.value != "") {
    sectionPurposeIsIdentifierDone.value = true;
    emit("update:identifier", identifier.value);
  } else {
    alert(organizationIdentifierLabel.value ? "You must provide " + organizationIdentifierLabel.value : "You must provide Identifier");
  }
};

const sectionOrgReset = () => {
  organizationId.value = null;
  identifier.value = null;
  sectionPurposeIsIdentifierDone.value = false;
  sectionContactVal.value = null;
  customerId.value = "";
  customerName.value = null;
  customerEmail.value = null;
};

const sectionContactVal = ref(null);
const sectionContactIsShow = computed(() => {
  if (shouldSkipOrgStep.value) {
    return isPreOrgStepComplete.value;
  }
  return sectionOrgIsSummary.value === true;
});
const sectionContactIsShowCustomers = computed(() => {
  return sectionContactVal.value == "Colleague";
});
const sectionContactIsShowExternal = computed(() => {
  return sectionContactVal.value == "External";
});
const sectionContactIsSummary = computed(() => {
  return sectionContactVal.value !== null && (sectionContactIsShowCustomers.value === false || (customerId.value !== null && customerId.value !== "")) && sectionContactIsShowExternal.value === false;
});
const sectionContactValLabel = computed(() => {
  return sectionContactVal.value === "You" ? sectionContactVal.value : "Someone Else";
});
const sectionContactSelect = val => {
  sectionContactVal.value = val;

  customerId.value = "";
  customerName.value = null;
  customerEmail.value = null;

  if (val == "You") {
    customerId.value = userId.value;
  } else if (val == "Colleague") {
    loadCustomers();
  }
};
const sectionContactReset = () => {
  sectionContactVal.value = null;
  customerId.value = "";
  customerName.value = null;
  customerEmail.value = null;
};

const sectionWizardIsShow = computed(() => {
  return sectionContactIsSummary.value === true || (sectionOrgIsSummary.value === true && sectionContactIsShowExternal.value === true);
});
const sectionWizardNote = computed(() => {
  if (sectionMethodIsSelfBooking.value === true) {
    return "Click next to use our self booking platform to choose your ideal flights and hotels. We have partnered with Expedia, Skyscanner and many others to offer best prices possible.";
  } else if (sectionItineraryIsTextBased.value == false) {
    return "Click next to use our assisted booking platform. This will capture your itinerary and preferences, then we will suggest the best flights, hotels and cars for you.";
  } else {
    return "Click next to create a concierge request. We will work with you to confirm your plans and handle all of the searching, booking and travel management for your team.";
  }
});

function validateEmail(email) {
  // eslint-disable-next-line no-useless-escape
  const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
}

const sectionWizardGo = () => {
  // Validate external contact
  if (sectionContactIsShowExternal.value === true && (customerName.value == null || customerEmail.value == null || customerName.value == "" || customerEmail.value == "" || !validateEmail(customerEmail.value))) {
    alert("Please provide a valid name and email for the external contact or change the main contact to yourself.");
    return;
  }

  if (sectionMethodIsSelfBooking.value === true) {
    emit("update:wizard", "book");
  } else if (sectionItineraryIsTextBased.value == false) {
    emit("update:wizard", "assist");
  } else {
    emit("update:wizard", "concierge");
  }
  emit("update:template", sectionTypeVal.value);
  emit("update:stage", "wizard");
};
</script>

<style lang="less">
#guide {
  .summary {
    color: @mid;
    display: flex;
    margin-bottom: 0.5rem;

    .tag {
      display: inline-block;
      flex: 1;
      width: 10rem;
    }

    a {
      border-left: 0.0625rem solid @border;
      margin-left: 0.5rem;
      padding-left: 0.5rem;
    }
  }

  .selection {
    h3 {
      margin-bottom: 2rem;
    }

    .radiolabel {
      align-items: center;
      background: @light;
      border: 0.0625rem solid @border;
      border-radius: 1rem;
      display: flex;
      margin-bottom: 1rem;
      padding: 1rem;

      .inner {
        padding-left: 1rem;

        label {
          padding: 0;
        }

        p {
          color: @mid;
          margin-top: 0.5rem;
        }
      }
    }

    .noselfbook {
      .dis {
        background: @secondary !important;
      }
    }
  }

  .wiz {
    p {
      margin-top: 2rem;
    }

    button {
      margin-top: 2rem;

      .icon {
        font-size: 0.75rem;
      }
    }
  }
}

@media screen and (min-width: 60em) {
  #guide {
    .selection {
      .radiolabel {
        &:hover {
          border-color: @primary;

          .radio:before {
            background: @primary;
          }
        }
      }

      .noselfbook {
        cursor: auto !important;

        &:hover {
          border-left-color: @light;

          .radio:before {
            background: @secondary;
          }
        }
      }
    }
  }
}
</style>
